import React from "react";

import { Text } from "@performyard/py-components";

const formatKey = (key: string) => {
	return (
		key
			// Handle consecutive uppercase letters followed by lowercase (like "ENPS" in "ENPSImprovement")
			.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
			// Handle regular camelCase (lowercase followed by uppercase)
			.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
			// Handle consecutive uppercase letters that aren't acronyms
			.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
			.replace(/^./, (str) => str.toUpperCase()) // capitalize first letter
			.replace(/_/g, " ")
	); // remove underscores
};

export const formatInsightText = (insight: Record<string, unknown>, indent = 0): string => {
	const pad = (level: number) => "  ".repeat(level);

	return Object.entries(insight)
		.map(([key, value]) => {
			if (value === "" || (Array.isArray(value) && value.length === 0)) return "";

			const formattedKey = formatKey(key);

			if (Array.isArray(value)) {
				const items = value.map((item) => `${pad(indent + 1)}• ${item}`).join("\n");
				return `${pad(indent)}${formattedKey}:\n${items}`;
			}

			if (typeof value === "object" && value !== null) {
				return `${pad(indent)}${formattedKey}:\n${formatInsightText(value as Record<string, unknown>, indent + 1)}`;
			}

			return `${pad(indent)}${formattedKey}: ${value}`;
		})
		.filter(Boolean)
		.join("\n");
};
export const InsightRenderer = ({
	insight,
	subValues,
	titleMap,
}: {
	insight: Record<string, unknown>;
	subValues?: boolean;
	titleMap?: Record<string, string>;
}) => {
	if (!insight) return null;

	//insight might be a string check if it is a parsable JSON object
	if (typeof insight === "string") {
		try {
			insight = JSON.parse(insight);
		} catch (error) {
			return null;
		}
	}

	if (typeof insight !== "object" || insight === null) {
		return null;
	}

	const renderValue = (value: any) => {
		if (Array.isArray(value)) {
			return (
				<ul className="list-disc space-y-1 pl-6 text-neutral-900 marker:text-xs">
					{value.map((item, index) => {
						if (Array.isArray(item)) {
							return (
								<li key={index} className="text-sm leading-4 font-normal">
									{item.map((subItem, subIndex) => (
										<span key={subIndex}>
											<span className="text-sm leading-4 font-normal whitespace-pre-wrap">
												{subItem}
											</span>
											<br />
										</span>
									))}
								</li>
							);
						} else {
							return (
								<li key={index} className="text-sm leading-4 font-normal">
									<span className="text-sm leading-4 font-normal whitespace-pre-wrap">{item}</span>
								</li>
							);
						}
					})}
				</ul>
			);
		}

		if (typeof value === "object" && value !== null) {
			return <InsightRenderer insight={value as Record<string, unknown>} subValues={true} titleMap={titleMap} />;
		}

		try {
			return (
				<Text
					size="paragraph-3"
					weight="regular"
					color="neutral-9"
					dangerouslySetClassName="whitespace-pre-wrap"
				>
					{value}
				</Text>
			);
		} catch (error) {
			return (
				<Text
					size="paragraph-3"
					weight="regular"
					color="neutral-9"
					dangerouslySetClassName="whitespace-pre-wrap"
				>
					{JSON.stringify(value)}
				</Text>
			);
		}
	};

	return (
		<div className="flex flex-col gap-2">
			{Object.entries(insight).map(([key, value]) => {
				if (value === "" || (Array.isArray(value) && value.length === 0)) {
					return null;
				}

				const displayTitle = titleMap?.[key] ?? formatKey(key);

				if (subValues) {
					return (
						<div key={key} className="flex flex-col gap-2 pl-6">
							<Text
								size="paragraph-3"
								weight="semi-bold"
								color="neutral-9"
								dangerouslySetClassName="capitalize"
							>
								{displayTitle}:
							</Text>
							{renderValue(value)}
						</div>
					);
				} else {
					return (
						<div key={key} className="mb-4 flex flex-col gap-2">
							<Text
								size="paragraph-3"
								weight="semi-bold"
								color="neutral-9"
								dangerouslySetClassName="capitalize"
							>
								{displayTitle}:
							</Text>
							{renderValue(value)}
						</div>
					);
				}
			})}
		</div>
	);
};
